import { action, makeObservable, observable } from 'mobx';

import { devLog } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import type { UserSnappyGiftData } from './types';
import { fetchUserSnappyGifts } from './utils';

export class UserSnappyGiftsStore extends Loadable implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);
  }

  private dialogsQueue: string[] = [];

  dialogTypes: FeatureManagerDialogType[] = ['UserSnappyGifts'];

  @observable
  public selectedSnappyGift: UserSnappyGiftData | null = null;

  @observable
  public userSnappyGiftsData: UserSnappyGiftData[] = [];

  @observable
  public isDialogOpen = false;

  @action
  public openDialog() {
    this.isDialogOpen = true;
  }

  @action
  public closeDialog() {
    this.selectedSnappyGift = null;
    this.isDialogOpen = false;
  }

  @action
  private setUserSnappyGiftsData(data: UserSnappyGiftData[]) {
    this.userSnappyGiftsData = data;
  }

  @action
  public reset() {
    this.userSnappyGiftsData = [];
    this.selectedSnappyGift = null;
    this.isDialogOpen = false;
    this.dialogsQueue = [];
    this.setLoading(false);
  }

  @action
  public openSnappyGiftDialogById(id: string) {
    const snappyGift = this.userSnappyGiftsData.find((gift) => gift.id === id);
    if (snappyGift) {
      this.selectedSnappyGift = snappyGift;
      this.openDialog();
    }
  }

  public startDialogs(type?: FeatureManagerDialogType) {
    if (type === 'UserSnappyGifts' && this.rootStore.authStore.authorized) {
      return this.showNextDialog();
    }

    return false;
  }

  public showNextDialog() {
    if (this.dialogsQueue.length > 0) {
      this.openSnappyGiftDialogById(this.dialogsQueue.shift()!);
      return true;
    }
    return false;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    this.setLoading(true);
    try {
      const snappyGiftsResponse = await fetchUserSnappyGifts();

      this.setUserSnappyGiftsData(snappyGiftsResponse.data.snappyGifts || []);
      this.dialogsQueue = this.userSnappyGiftsData.map((gift) => gift.id);
      return 'READY';
    } catch (error) {
      devLog('Error fetching user free spins', error);
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  });
}
